import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import {
  SqlResultsTable,
  parseSqlResult,
  type SqlResultData,
} from '@/components/SqlResultsTable'
import { useSelectedAgent } from '@/contexts/SelectedAgentContext'
import { useConversationMessages } from '@/hooks/useConversationsQuery'
import type { ToolCallDisplay } from '@/types'

function Message({ children }: { children: string }) {
  return (
    <div className="flex items-center justify-center h-full text-sm text-gray-500">
      {children}
    </div>
  )
}

/**
 * The results tab is URL-true: nav state is only a fast path for the first
 * render. A backgrounded tab keeps its href but loses location.state, so the
 * link also carries `agentId` + `conversation` + `toolCallId` and the payload
 * is re-read from the conversation. (`agentId` and `conversation`, not the
 * `agent` / `conversationId` shell keys — taskSearchOf strips shell keys from
 * the stored tab href.)
 *
 * Accepted edge: a tab backgrounded during an active stream can be served the
 * cached message list, which does not hold the running turn until the
 * post-stream invalidate. Its results read as "no longer available" until then.
 */
export function QueryResultsRoute() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { selectedAgentId } = useSelectedAgent()

  const stateResult = (location.state as { result?: SqlResultData } | null)
    ?.result
  // The tab owns its agent; the context is the fallback for a link built
  // without the param.
  const agentParam = searchParams.get('agentId')
  const agentId =
    agentParam === 'david' || agentParam === 'hanna'
      ? agentParam
      : selectedAgentId
  const conversationId = searchParams.get('conversation') ?? undefined
  const toolCallId = searchParams.get('toolCallId')

  // Withholding the id keeps the query disabled through the hook's own
  // `enabled: !!session && !!conversationId`, so the fast path (and the
  // half-a-link case) makes no round trip.
  const {
    data: conversation,
    isLoading,
    isError,
  } = useConversationMessages(
    agentId,
    stateResult || !toolCallId ? undefined : conversationId
  )

  // Parse once per payload: any navigation re-renders this tree, and a fresh
  // result object each time would defeat SqlResultsTable's memos.
  const result = useMemo(() => {
    const call = conversation?.events.find(
      (event): event is ToolCallDisplay =>
        event.type === 'tool_call' && event.toolCallId === toolCallId
    )
    return parseSqlResult(call?.result)
  }, [conversation, toolCallId])

  if (stateResult) return <SqlResultsTable result={stateResult} />
  if (!conversationId || !toolCallId) {
    return <Message>No query results to display.</Message>
  }
  if (isLoading) return <Message>Loading query results…</Message>
  if (isError) return <Message>Could not load query results.</Message>
  if (!result) {
    return <Message>These query results are no longer available.</Message>
  }
  return <SqlResultsTable result={result} />
}
